import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Trash2, Plus } from 'lucide-react'
import { useCardStore } from '../store/cardStore'
import type { Card, SocialLink } from '../types'
import axios from 'axios'
import { useThemeStore } from '../store/themeStore'
import { apiBaseUrl } from '../config'

interface CardEditModalProps {
  card: Card
  isOpen: boolean
  onClose: () => void
}

const SOCIAL_TYPES = ['spotify', 'linkedin', 'discord', 'instagram', 'twitter', 'tiktok', 'youtube', 'github', 'soundcloud'] as const

export default function CardEditModal({ card, isOpen, onClose }: CardEditModalProps) {
  const { updateCard, deleteCard, subpages } = useCardStore()
  const theme = useThemeStore((s) => s.theme)
  const [content, setContent] = useState(card.content)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [paletteOrder, setPaletteOrder] = useState<Array<{ idx: 1 | 2 | 3 | 4; color: string }>>([
    { idx: 1, color: 'var(--card-bg-1)' },
    { idx: 2, color: 'var(--card-bg-2)' },
    { idx: 3, color: 'var(--card-bg-3)' },
    { idx: 4, color: 'var(--card-bg-4)' },
  ])

  useEffect(() => {
    setContent(card.content)
  }, [card])

  useEffect(() => {
    if (!isOpen) return
    if (theme === 'mono' || theme === 'glass') return

    const css = getComputedStyle(document.documentElement)
    const read = (i: 1 | 2 | 3 | 4) => css.getPropertyValue(`--card-bg-${i}`).trim() || `var(--card-bg-${i})`

    const parseToRgb = (raw: string): { r: number; g: number; b: number } | null => {
      const s = raw.trim()
      if (!s) return null
      if (s.startsWith('#')) {
        const hex = s.slice(1)
        const full = hex.length === 3
          ? hex.split('').map((c) => c + c).join('')
          : hex
        if (full.length !== 6) return null
        const n = parseInt(full, 16)
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
      }
      const m = s.match(/rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)/i)
      if (m) return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) }
      return null
    }

    const luminance = (rgb: { r: number; g: number; b: number }) => {
      const toLin = (v: number) => {
        const s = v / 255
        return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
      }
      const r = toLin(rgb.r)
      const g = toLin(rgb.g)
      const b = toLin(rgb.b)
      return 0.2126 * r + 0.7152 * g + 0.0722 * b
    }

    const items = ([1, 2, 3, 4] as const).map((idx) => {
      const color = read(idx)
      const rgb = parseToRgb(color)
      const lum = rgb ? luminance(rgb) : 0
      return { idx, color, lum }
    })

    items.sort((a, b) => a.lum - b.lum)
    setPaletteOrder(items.map(({ idx, color }) => ({ idx, color })))
  }, [isOpen, theme])

  const convertToWebpIfPossible = async (file: File): Promise<File> => {
    try {
      if (!file.type.startsWith('image/')) return file
      if (file.type === 'image/webp') return file
      if (typeof createImageBitmap !== 'function') return file

      const bitmap = await createImageBitmap(file)
      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return file
      ctx.drawImage(bitmap, 0, 0)

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/webp', 0.75)
      )
      if (!blob) return file

      const webpName = file.name.replace(/\.[^/.]+$/, '') + '.webp'
      return new File([blob], webpName, { type: 'image/webp' })
    } catch {
      return file
    }
  }

  const canEditCardPalette = theme !== 'mono' && theme !== 'glass'
  const selectedPaletteIndex = (content as any)?.paletteIndex as 1 | 2 | 3 | 4 | undefined
  const cleanSoundCloudTitle = (raw: string) => raw.replace(/\s+by\s+.+$/i, '').trim()

  const applySoundCloudMetadata = (baseContent: any, soundcloudUrl: string, data: any) => {
    const fetchedTitle = cleanSoundCloudTitle(((data?.title) || '').trim())
    const fetchedSubtext = ((data?.author_name) || '').trim()
    const fetchedCover = ((data?.thumbnail_url) || '').trim()

    const titleValue = fetchedTitle || (baseContent.audioTitle || baseContent.name || '').trim()
    const subtextValue = fetchedSubtext || (baseContent.audioSubtext || baseContent.subtext || '').trim()
    const coverValue = fetchedCover || (baseContent.audioCoverUrl || baseContent.cover || '').trim()

    return {
      ...baseContent,
      audioSoundcloudUrl: soundcloudUrl,
      audioUrl: '',
      audioKey: '',
      audioTitle: titleValue,
      name: titleValue,
      audioSubtext: subtextValue,
      subtext: subtextValue,
      audioCoverUrl: coverValue,
      cover: coverValue,
      audioSoundcloudFetchedUrl: soundcloudUrl,
      audioSoundcloudMetaFetched: true,
    }
  }

  const uploadAudioFile = async (file: File) => {
    setUploadError('')
    if (!file) return

    setUploading(true)
    try {
      const presignRes = await axios.post('/api/uploads/audio/presign', {
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })

      const uploadUrl = presignRes.data?.uploadUrl as string | undefined
      const publicUrl = presignRes.data?.publicUrl as string | undefined
      const key = presignRes.data?.key as string | undefined
      const cacheControl = presignRes.data?.cacheControl as string | undefined

      if (!uploadUrl || !publicUrl || !key) {
        throw new Error('Upload configuration missing')
      }

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          ...(cacheControl ? { 'Cache-Control': cacheControl } : {}),
        },
        body: file,
      })
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`)
      }

      await axios.post('/api/uploads/audio/commit', {
        key,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      })

      const updatedContent = {
        ...content,
        audioUrl: publicUrl,
        audioKey: key,
        audioSoundcloudUrl: '',
        audioSoundcloudFetchedUrl: '',
        audioSoundcloudMetaFetched: false,
      }

      setContent(updatedContent)
      await updateCard(card.id, { content: updatedContent })
    } catch (err: any) {
      const apiError = err?.response?.data?.error
      if (apiError === 'quota_exceeded') {
        setUploadError('Upload limit reached (15MB total).')
      } else if (apiError === 'file_too_large') {
        setUploadError('File is too large.')
      } else {
        setUploadError('Upload failed.')
      }
    } finally {
      setUploading(false)
    }
  }

  const fetchSoundCloudMetadata = async (rawUrl: string) => {
    const trimmed = rawUrl.trim().replace(/^\/+/, '')
    if (!trimmed) return null
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const configuredBase = (apiBaseUrl || '').trim().replace(/\/$/, '')
    const devBackendBase = import.meta.env.DEV && typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.hostname}:8080`
      : ''
    const endpoints = Array.from(
      new Set([
        '/api/soundcloud/oembed',
        configuredBase ? `${configuredBase}/api/soundcloud/oembed` : '',
        devBackendBase ? `${devBackendBase}/api/soundcloud/oembed` : '',
      ].filter(Boolean))
    )

    let lastError: any = null
    for (const endpoint of endpoints) {
      try {
        // Use default axios (with credentials) for our own backend proxy
        const res = await axios.get(endpoint, { params: { url: normalized } })
        return res.data || null
      } catch (err: any) {
        lastError = err
        const status = err?.response?.status
        if (status && status !== 404) throw err
      }
    }

    // Direct SoundCloud oembed call — must NOT send credentials because
    // SoundCloud returns Access-Control-Allow-Origin: * which is
    // incompatible with credentials mode "include" per the CORS spec.
    try {
      const plainAxios = axios.create({ withCredentials: false })
      const res = await plainAxios.get('https://soundcloud.com/oembed', {
        params: {
          format: 'json',
          url: normalized,
          maxheight: 166,
        },
      })
      return res.data || null
    } catch (err: any) {
      lastError = err
    }

    throw lastError || new Error('SoundCloud metadata endpoint is unreachable')
  }

  const uploadPhotoFile = async (file: File) => {
    setUploadError('')
    if (!file) return

    setUploading(true)
    try {
      const uploadFile = await convertToWebpIfPossible(file)

      const presignRes = await axios.post('/api/uploads/photo/presign', {
        filename: uploadFile.name,
        contentType: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
      })

      const uploadUrl = presignRes.data?.uploadUrl as string | undefined
      const publicUrl = presignRes.data?.publicUrl as string | undefined
      const key = presignRes.data?.key as string | undefined
      const cacheControl = presignRes.data?.cacheControl as string | undefined

      if (!uploadUrl || !publicUrl || !key) {
        throw new Error('Upload configuration missing')
      }

      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': uploadFile.type || 'application/octet-stream',
          ...(cacheControl ? { 'Cache-Control': cacheControl } : {}),
        },
        body: uploadFile,
      })
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`)
      }

      await axios.post('/api/uploads/photo/commit', {
        key,
        contentType: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
      })

      const prevImages = Array.isArray((content as any).images) ? ([...(content as any).images] as string[]) : []
      const prevKeys = Array.isArray((content as any).imageKeys) ? ([...(content as any).imageKeys] as string[]) : []

      const nextImages = [...prevImages, publicUrl]
      const nextKeys = [...prevKeys, key]

      const updatedContent = {
        ...content,
        imageUrl: nextImages.length === 1 ? publicUrl : (content as any).imageUrl,
        imageKey: nextKeys.length === 1 ? key : (content as any).imageKey,
        images: nextImages,
        imageKeys: nextKeys,
      }

      setContent(updatedContent)

      // Immediately save to backend so image appears without refresh and deletions can be tracked
      await updateCard(card.id, { content: updatedContent })
    } catch (err: any) {
      const apiError = err?.response?.data?.error
      if (apiError === 'quota_exceeded') {
        setUploadError('Upload limit reached (15MB total).')
      } else if (apiError === 'file_too_large') {
        setUploadError('File is too large.')
      } else {
        setUploadError('Upload failed.')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      let nextContent = content as any
      const rawSoundcloudUrl = ((nextContent as any).audioSoundcloudUrl || '').trim().replace(/^\/+/, '')
      const soundcloudUrl = /^https?:\/\//i.test(rawSoundcloudUrl) ? rawSoundcloudUrl : (rawSoundcloudUrl ? `https://${rawSoundcloudUrl}` : '')
      const shouldFetchSoundcloudMeta =
        card.card_type === 'audio' &&
        soundcloudUrl &&
        (
          !(nextContent as any).audioSoundcloudMetaFetched ||
          ((nextContent as any).audioSoundcloudFetchedUrl || '').trim() !== soundcloudUrl
        )

      if (card.card_type === 'audio' && soundcloudUrl) {
        nextContent = {
          ...nextContent,
          audioSoundcloudUrl: soundcloudUrl,
          audioUrl: '',
          audioKey: '',
        }
        if (shouldFetchSoundcloudMeta) {
          nextContent = {
            ...nextContent,
            audioSoundcloudFetchedUrl: '',
            audioSoundcloudMetaFetched: false,
          }
        }
      }

      setContent(nextContent)
      await updateCard(card.id, { content: nextContent })

      if (shouldFetchSoundcloudMeta) {
        void (async () => {
          try {
            const data = await fetchSoundCloudMetadata(soundcloudUrl)
            const hydratedContent = applySoundCloudMetadata(nextContent, soundcloudUrl, data)
            await updateCard(card.id, { content: hydratedContent })
          } catch {
            setUploadError('SoundCloud metadata could not be fetched.')
          }
        })()
      }
    } catch (error) {
      console.error('Failed to save:', error)
    } finally {
      setSaving(false)
    }
    onClose()
  }

  const handleDelete = async () => {
    try {
      await deleteCard(card.id)
      onClose()
    } catch (error) {
      console.error('Failed to delete:', error)
    } finally {
      setConfirmDelete(false)
    }
  }

  const applyPaletteIndex = async (paletteIndex: 1 | 2 | 3 | 4) => {
    const nextContent: any = { ...content, paletteIndex }

    setContent(nextContent)
    try {
      await updateCard(card.id, { content: nextContent })
    } catch (error) {
      console.error('Failed to update palette:', error)
    }
  }

  const updateContent = (key: string, value: unknown) => {
    setContent((prev) => ({ ...prev, [key]: value }))
  }

  const AlignButton = ({
    active,
    onClick,
    label,
    children,
  }: {
    active: boolean
    onClick: () => void
    label: string
    children: React.ReactNode
  }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${active
        ? 'bg-white/15 text-nok-white'
        : 'text-nok-gray-500 hover:text-nok-gray-300 hover:bg-white/5'
        }`}
    >
      {children}
    </button>
  )

  const HAlignIcon = ({ align }: { align: 'left' | 'center' | 'right' }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      <line x1={align === 'right' ? 4 : 2} y1="3" x2="14" y2="3" stroke="currentColor" strokeWidth="1.5" />
      <line x1={align === 'left' ? 2 : align === 'center' ? 4 : 6} y1="7" x2={align === 'right' ? 14 : align === 'center' ? 12 : 10} y2="7" stroke="currentColor" strokeWidth="1.5" />
      <line x1={align === 'right' ? 4 : 2} y1="11" x2="14" y2="11" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )

  const VAlignIcon = ({ align }: { align: 'top' | 'center' }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      {align === 'top' ? (
        <>
          <line x1="2" y1="2" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5" />
          <rect x="5" y="5" width="6" height="4" rx="1" fill="currentColor" opacity="0.5" />
        </>
      ) : (
        <>
          <rect x="5" y="5.5" width="6" height="5" rx="1" fill="currentColor" opacity="0.5" />
          <line x1="3" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="2" opacity="0.4" />
          <line x1="13" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="2" opacity="0.4" />
        </>
      )}
    </svg>
  )

  const renderFields = () => {
    switch (card.card_type) {
      case 'profile':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Name</label>
              <input
                type="text"
                value={content.name || ''}
                onChange={(e) => updateContent('name', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Nickname</label>
              <input
                type="text"
                value={content.nickname || ''}
                onChange={(e) => updateContent('nickname', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Font Size</label>
              <select
                value={content.fontSize || 'medium'}
                onChange={(e) => updateContent('fontSize', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Alignment</label>
              <div className="flex">
                <AlignButton
                  active={(content.profileAlign || 'left') === 'left'}
                  onClick={() => updateContent('profileAlign', 'left')}
                  label="Align left"
                >
                  <HAlignIcon align="left" />
                </AlignButton>
                <AlignButton
                  active={(content.profileAlign || 'left') === 'center'}
                  onClick={() => updateContent('profileAlign', 'center')}
                  label="Align center"
                >
                  <HAlignIcon align="center" />
                </AlignButton>
                <AlignButton
                  active={(content.profileAlign || 'left') === 'right'}
                  onClick={() => updateContent('profileAlign', 'right')}
                  label="Align right"
                >
                  <HAlignIcon align="right" />
                </AlignButton>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Social Links</label>
              {(content.socialLinks || []).map((link: SocialLink, index: number) => (
                <div key={index} className="flex gap-2">
                  <select
                    value={link.type}
                    onChange={(e) => {
                      const newLinks = [...(content.socialLinks || [])]
                      newLinks[index] = { ...link, type: e.target.value as SocialLink['type'] }
                      updateContent('socialLinks', newLinks)
                    }}
                    className="bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-2 py-2 font-serif text-sm"
                  >
                    {SOCIAL_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => {
                      const newLinks = [...(content.socialLinks || [])]
                      newLinks[index] = { ...link, url: e.target.value }
                      updateContent('socialLinks', newLinks)
                    }}
                    placeholder="URL"
                    className="flex-1 bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-2 py-2 font-serif text-sm"
                  />
                  <button
                    onClick={() => {
                      const newLinks = (content.socialLinks || []).filter((_: SocialLink, i: number) => i !== index)
                      updateContent('socialLinks', newLinks)
                    }}
                    className="p-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newLinks = [...(content.socialLinks || []), { type: 'instagram', url: '' }]
                  updateContent('socialLinks', newLinks)
                }}
                className="flex items-center gap-1 text-sm text-nok-gray-400 hover:text-nok-white"
              >
                <Plus size={14} /> Add Link
              </button>
            </div>
          </>
        )

      case 'link':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Label</label>
              <input
                type="text"
                value={content.label || ''}
                onChange={(e) => updateContent('label', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Align</label>
              <div className="relative w-full bg-nok-gray-800 border border-nok-gray-600 h-10 flex items-center">
                <div
                  className="absolute top-0 bottom-0 w-1/2 bg-nok-gray-700 transition-transform"
                  style={{ transform: (content.linkAlign || 'right') === 'left' ? 'translateX(0%)' : 'translateX(100%)' }}
                />
                <button
                  type="button"
                  onClick={() => updateContent('linkAlign', 'left')}
                  className={`relative z-10 w-1/2 h-full font-serif text-sm ${(content.linkAlign || 'right') === 'left' ? 'text-nok-white' : 'text-nok-gray-400'
                    }`}
                >
                  Left
                </button>
                <button
                  type="button"
                  onClick={() => updateContent('linkAlign', 'right')}
                  className={`relative z-10 w-1/2 h-full font-serif text-sm ${(content.linkAlign || 'right') === 'right' ? 'text-nok-white' : 'text-nok-gray-400'
                    }`}
                >
                  Right
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">URL</label>
              <input
                type="url"
                value={content.url || ''}
                onChange={(e) => {
                  updateContent('url', e.target.value)
                  if (e.target.value) updateContent('linkSubpage', '')
                }}
                disabled={!!content.linkSubpage}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif disabled:opacity-40"
              />
            </div>
            {subpages.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm text-nok-gray-400 font-serif">Or redirect to page</label>
                <select
                  value={content.linkSubpage || ''}
                  onChange={(e) => {
                    updateContent('linkSubpage', e.target.value)
                    if (e.target.value) updateContent('url', '')
                  }}
                  className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
                >
                  <option value="">— External URL —</option>
                  <option value="__main__">Main</option>
                  {subpages.map((sp) => (
                    <option key={sp.id} value={sp.slug}>/{sp.slug}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )

      case 'text':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Text</label>
              <textarea
                value={content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                rows={5}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif resize-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="space-y-2 flex-shrink-0">
                <label className="text-sm text-nok-gray-400 font-serif">Text Size</label>
                <select
                  value={content.textFontSize || 'small'}
                  onChange={(e) => updateContent('textFontSize', e.target.value)}
                  className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div className="space-y-2 min-w-0">
                <label className="text-sm text-nok-gray-400 font-serif">Alignment</label>
                <div className="flex gap-1">
                  <AlignButton
                    active={(content.textAlign || 'left') === 'left'}
                    onClick={() => updateContent('textAlign', 'left')}
                    label="Align left"
                  >
                    <HAlignIcon align="left" />
                  </AlignButton>
                  <AlignButton
                    active={(content.textAlign || 'left') === 'center'}
                    onClick={() => updateContent('textAlign', 'center')}
                    label="Align center"
                  >
                    <HAlignIcon align="center" />
                  </AlignButton>
                  <AlignButton
                    active={(content.textAlign || 'left') === 'right'}
                    onClick={() => updateContent('textAlign', 'right')}
                    label="Align right"
                  >
                    <HAlignIcon align="right" />
                  </AlignButton>
                  <div className="w-px bg-white/10 mx-0.5" />
                  <AlignButton
                    active={(content.textVAlign || 'top') === 'top'}
                    onClick={() => updateContent('textVAlign', 'top')}
                    label="Align top"
                  >
                    <VAlignIcon align="top" />
                  </AlignButton>
                  <AlignButton
                    active={(content.textVAlign || 'top') === 'center'}
                    onClick={() => updateContent('textVAlign', 'center')}
                    label="Align center (vertical)"
                  >
                    <VAlignIcon align="center" />
                  </AlignButton>
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <label className="text-sm text-nok-gray-400 font-serif">Appearance</label>
              <button
                type="button"
                onClick={() => updateContent('textTransparent', !content.textTransparent)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif flex items-center justify-between"
              >
                <span className="opacity-80">Transparent background</span>
                <span
                  className={`inline-flex items-center px-2 py-1 border text-xs font-bold ${content.textTransparent
                    ? 'bg-nok-white text-nok-black border-nok-white'
                    : 'bg-transparent text-nok-white border-nok-gray-600'
                    }`}
                >
                  {content.textTransparent ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          </>
        )

      case 'photo':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Upload Photo</label>
              <div
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-4 font-serif text-sm"
                onDragOver={(e) => {
                  e.preventDefault()
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const files = Array.from(e.dataTransfer.files || [])
                  for (const f of files) void uploadPhotoFile(f)
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="opacity-80">Drag & drop an image here</span>
                  <label className="cursor-pointer px-3 py-2 bg-nok-white text-nok-black font-serif font-bold">
                    {uploading ? 'Uploading…' : 'Choose File'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || [])
                        for (const f of files) void uploadPhotoFile(f)
                        e.currentTarget.value = ''
                      }}
                    />
                  </label>
                </div>
                {uploadError && <p className="text-red-400 text-sm mt-2">{uploadError}</p>}
              </div>
            </div>

            {Array.isArray((content as any).images) && (content as any).images.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm text-nok-gray-400 font-serif">Uploaded Images</label>
                <div className="space-y-2">
                  {(content as any).images.map((url: string, index: number) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="url"
                        value={url}
                        readOnly
                        className="flex-1 bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-2 py-2 font-serif text-sm opacity-80"
                      />
                      <button
                        onClick={async () => {
                          const prevImages = Array.isArray((content as any).images) ? ([...(content as any).images] as string[]) : []
                          const prevKeys = Array.isArray((content as any).imageKeys) ? ([...(content as any).imageKeys] as string[]) : []
                          const nextImages = prevImages.filter((_: string, i: number) => i !== index)
                          const nextKeys = prevKeys.filter((_: string, i: number) => i !== index)
                          const updatedContent = {
                            ...content,
                            images: nextImages,
                            imageKeys: nextKeys,
                            imageUrl: nextImages.length === 1 ? nextImages[0] : '',
                            imageKey: nextKeys.length === 1 ? nextKeys[0] : '',
                          }
                          setContent(updatedContent)
                          await updateCard(card.id, { content: updatedContent })
                        }}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Image URL</label>
              <input
                type="url"
                value={content.imageUrl || ''}
                onChange={(e) => updateContent('imageUrl', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Link URL (optional)</label>
              <input
                type="url"
                value={content.photoLinkUrl || ''}
                onChange={(e) => updateContent('photoLinkUrl', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Filter</label>
              <button
                type="button"
                onClick={() => {
                  const current = ((content as any).photoFilter || 'none') as 'none' | 'dither'
                  updateContent('photoFilter', current === 'dither' ? 'none' : 'dither')
                }}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif flex items-center justify-between"
              >
                <span className="opacity-80">Theme filter</span>
                <span
                  className={`inline-flex items-center px-2 py-1 border text-xs font-bold ${((content as any).photoFilter || 'none') === 'dither'
                    ? 'bg-nok-white text-nok-black border-nok-white'
                    : 'bg-transparent text-nok-white border-nok-gray-600'
                    }`}
                >
                  {((content as any).photoFilter || 'none') === 'dither' ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Photo zoom</label>
              <button
                type="button"
                onClick={() => {
                  const disabled = Boolean((content as any).photoZoomDisabled)
                  updateContent('photoZoomDisabled', !disabled)
                }}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif flex items-center justify-between"
              >
                <span className="opacity-80">Disable click to enlarge</span>
                <span
                  className={`inline-flex items-center px-2 py-1 border text-xs font-bold ${Boolean((content as any).photoZoomDisabled)
                    ? 'bg-nok-white text-nok-black border-nok-white'
                    : 'bg-transparent text-nok-white border-nok-gray-600'
                    }`}
                >
                  {Boolean((content as any).photoZoomDisabled) ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>
          </>
        )

      case 'spotify_top':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Time Range</label>
              <select
                value={content.spotifyTimeRange || 'short_term'}
                onChange={(e) => updateContent('spotifyTimeRange', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              >
                <option value="short_term">This Month</option>
                <option value="medium_term">Last 6 Months</option>
                <option value="long_term">All Time</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Number of Songs (1-5)</label>
              <input
                type="number"
                min={1}
                max={5}
                value={content.songCount || 3}
                onChange={(e) => updateContent('songCount', Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
          </>
        )

      case 'video':
        return (
          <div className="space-y-2">
            <label className="text-sm text-nok-gray-400 font-serif">YouTube URL</label>
            <input
              type="url"
              value={content.videoUrl || ''}
              onChange={(e) => updateContent('videoUrl', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
            />
          </div>
        )

      case 'audio':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Song name (optional)</label>
              <input
                type="text"
                value={(content as any).audioTitle || (content as any).name || ''}
                onChange={(e) => {
                  const nextValue = e.target.value
                  setContent((prev: any) => ({ ...prev, audioTitle: nextValue, name: nextValue }))
                }}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Subtext (optional)</label>
              <input
                type="text"
                value={(content as any).audioSubtext || (content as any).subtext || ''}
                onChange={(e) => {
                  const nextValue = e.target.value
                  setContent((prev: any) => ({ ...prev, audioSubtext: nextValue, subtext: nextValue }))
                }}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Cover Art (optional)</label>
              <div className="flex items-start gap-3">
                {((content as any).audioCoverUrl || (content as any).cover) ? (
                  <div className="relative w-16 h-16 bg-nok-gray-800">
                    <img
                      src={(content as any).audioCoverUrl || (content as any).cover}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={async () => {
                        const updatedContent = { ...content, audioCoverUrl: '', cover: '', audioCoverKey: '' }
                        setContent(updatedContent)
                        await updateCard(card.id, { content: updatedContent })
                      }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer w-16 h-16 bg-nok-gray-800 border border-nok-gray-600 flex items-center justify-center text-nok-gray-400 hover:border-nok-gray-400 transition-colors">
                    {uploading ? '...' : '+'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]
                        if (!f) return
                        e.currentTarget.value = ''
                        setUploading(true)
                        setUploadError('')
                        try {
                          const uploadFile = await convertToWebpIfPossible(f)
                          const presignRes = await axios.post('/api/uploads/photo/presign', {
                            filename: uploadFile.name,
                            contentType: uploadFile.type || 'application/octet-stream',
                            size: uploadFile.size,
                          })
                          const uploadUrl = presignRes.data?.uploadUrl as string | undefined
                          const publicUrl = presignRes.data?.publicUrl as string | undefined
                          const key = presignRes.data?.key as string | undefined
                          const cacheControl = presignRes.data?.cacheControl as string | undefined
                          if (!uploadUrl || !publicUrl || !key) throw new Error('Upload config missing')
                          const putRes = await fetch(uploadUrl, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': uploadFile.type || 'application/octet-stream',
                              ...(cacheControl ? { 'Cache-Control': cacheControl } : {}),
                            },
                            body: uploadFile,
                          })
                          if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`)
                          await axios.post('/api/uploads/photo/commit', {
                            key,
                            contentType: uploadFile.type || 'application/octet-stream',
                            size: uploadFile.size,
                          })
                          const updatedContent = { ...content, audioCoverUrl: publicUrl, cover: publicUrl, audioCoverKey: key }
                          setContent(updatedContent)
                          await updateCard(card.id, { content: updatedContent })
                        } catch (err: any) {
                          setUploadError('Cover upload failed.')
                        } finally {
                          setUploading(false)
                        }
                      }}
                    />
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const current = (content as any).audioShowTextOnCard
                    const next = current === undefined ? false : !Boolean(current)
                    updateContent('audioShowTextOnCard', next)
                  }}
                  className="min-h-16 flex-1 bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif flex items-center justify-between hover:border-nok-gray-500 transition-colors"
                >
                  <span className="pr-3 text-left text-sm opacity-80">Show text on card</span>
                  <span
                    className={`inline-flex items-center px-2 py-1 border text-xs font-bold ${((content as any).audioShowTextOnCard ?? true)
                      ? 'bg-nok-white text-nok-black border-nok-white'
                      : 'bg-transparent text-nok-white border-nok-gray-600'
                      }`}
                  >
                    {((content as any).audioShowTextOnCard ?? true) ? 'ON' : 'OFF'}
                  </span>
                </button>
              </div>
            </div>


            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Filter</label>
              <button
                type="button"
                onClick={() => {
                  const current = ((content as any).audioFilter || 'none') as 'none' | 'theme'
                  updateContent('audioFilter', current === 'theme' ? 'none' : 'theme')
                }}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif flex items-center justify-between"
              >
                <span className="opacity-80">Theme filter</span>
                <span
                  className={`inline-flex items-center px-2 py-1 border text-xs font-bold ${((content as any).audioFilter || 'none') === 'theme'
                    ? 'bg-nok-white text-nok-black border-nok-white'
                    : 'bg-transparent text-nok-white border-nok-gray-600'
                    }`}
                >
                  {((content as any).audioFilter || 'none') === 'theme' ? 'ON' : 'OFF'}
                </span>
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Upload Audio</label>
              <div
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-4 font-serif text-sm"
                onDragOver={(e) => {
                  e.preventDefault()
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const files = Array.from(e.dataTransfer.files || [])
                  for (const f of files) void uploadAudioFile(f)
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="opacity-80">Drag & drop an audio file here</span>
                  <label className="cursor-pointer px-3 py-2 bg-nok-white text-nok-black font-serif font-bold">
                    {uploading ? 'Uploading…' : 'Choose File'}
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) void uploadAudioFile(f)
                        e.currentTarget.value = ''
                      }}
                    />
                  </label>
                </div>
                {uploadError && <p className="text-red-400 text-sm mt-2">{uploadError}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Or SoundCloud Link</label>
              <div className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-3 font-serif text-sm space-y-3">
                <input
                  type="url"
                  value={(content as any).audioSoundcloudUrl || ''}
                  onChange={(e) => {
                    setUploadError('')
                    const nextValue = e.target.value
                    if (!nextValue.trim()) {
                      const updatedContent = {
                        ...content,
                        audioSoundcloudUrl: '',
                        audioUrl: '',
                        audioKey: '',
                        audioSoundcloudFetchedUrl: '',
                        audioSoundcloudMetaFetched: false,
                      }
                      setContent(updatedContent)
                      void updateCard(card.id, { content: updatedContent })
                      return
                    }
                    setContent((prev) => ({
                      ...prev,
                      audioSoundcloudUrl: nextValue,
                      audioSoundcloudFetchedUrl: '',
                      audioSoundcloudMetaFetched: false,
                    }))
                  }}
                  placeholder="https://soundcloud.com/artist/track"
                  className="w-full bg-black/20 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
                />
              </div>
            </div>

            {(content as any).audioUrl && (
              <div className="space-y-2">
                <label className="text-sm text-nok-gray-400 font-serif">Audio File</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={(content as any).audioUrl || ''}
                    readOnly
                    className="flex-1 bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif opacity-80 text-sm truncate"
                  />
                  <button
                    onClick={async () => {
                      const updatedContent = { ...content, audioUrl: '', audioKey: '' }
                      setContent(updatedContent)
                      await updateCard(card.id, { content: updatedContent })
                    }}
                    className="px-3 py-2 border border-red-500 text-red-400 font-serif hover:bg-red-500/10 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

          </>
        )

      case 'social_icon':
        return (
          <>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">Platform</label>
              <select
                value={content.socialIconType || 'instagram'}
                onChange={(e) => updateContent('socialIconType', e.target.value)}
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              >
                {SOCIAL_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-nok-gray-400 font-serif">URL</label>
              <input
                type="url"
                value={content.socialIconUrl || ''}
                onChange={(e) => updateContent('socialIconUrl', e.target.value)}
                placeholder="https://..."
                className="w-full bg-nok-gray-800 border border-nok-gray-600 text-nok-white px-3 py-2 font-serif"
              />
            </div>
          </>
        )

      default:
        return <p className="text-nok-gray-400 font-serif">No editable properties</p>
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full max-w-sm bg-[#0a0a0a] border border-white/10 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
              <span className="font-serif text-sm text-white/50 uppercase tracking-wider">
                {card.card_type.replace('_', ' ')}
              </span>

              <div className="flex items-center gap-2">
                {canEditCardPalette && (
                  <div className="flex items-center gap-1.5">
                    {paletteOrder.map(({ idx, color }) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => applyPaletteIndex(idx)}
                        title={`Color ${idx}`}
                        aria-label={`Color ${idx}`}
                        className="w-3 h-3 rounded-full border transition-colors"
                        style={{
                          background: color,
                          borderColor:
                            selectedPaletteIndex === idx
                              ? 'rgba(255,255,255,0.95)'
                              : 'rgba(255,255,255,0.25)',
                        }}
                      />
                    ))}
                  </div>
                )}

                <button
                  onClick={onClose}
                  className="p-1 text-white/40 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto px-4 py-3 space-y-3">
              {renderFields()}
            </div>

            <div className="px-4 py-3 border-t border-white/10 flex-shrink-0">
              {confirmDelete ? (
                <div className="space-y-2">
                  <p className="text-white/60 font-serif text-xs">Silmek istediğine emin misin?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 py-2 border border-white/10 text-white/60 font-serif text-sm hover:border-white/25 transition-colors"
                    >
                      Vazgeç
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex-1 py-2 border border-red-500/50 text-red-400 font-serif text-sm hover:bg-red-500/10 transition-colors"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="px-3 py-2 border border-red-500/40 text-red-400/80 font-serif text-sm hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 py-2 bg-white text-black font-serif text-sm font-bold hover:bg-white/90 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
